import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, X, AtSign } from "lucide-react";
import { chatApi } from "../lib/realtime";
import { OndaAvatar } from "./OndaAvatar";
import { VerifiedBadge } from "./VerifiedBadge";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";

export function UserSearch({ onOpenConversation }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [state, setState] = useState("idle"); // idle | searching | ready | empty
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const isEmailQuery = q.includes("@") && q.includes(".");

  useEffect(() => {
    const trimmed = q.trim();
    if (timerRef.current) clearTimeout(timerRef.current);
    if (trimmed.length < 2) {
      setResults([]);
      setState("idle");
      return;
    }
    setState("searching");
    timerRef.current = setTimeout(async () => {
      try {
        const data = await chatApi.searchUsers(trimmed);
        setResults(data);
        setState(data.length ? "ready" : "empty");
      } catch {
        setResults([]);
        setState("empty");
      }
    }, 320);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q]);

  const openChat = async (userId) => {
    setCreating(true);
    try {
      const conv = await chatApi.createConversation(userId);
      onOpenConversation && onOpenConversation(conv);
      navigate(`/chat/${conv.id}`);
      setQ("");
      setResults([]);
      setState("idle");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="relative" data-testid="user-search">
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          data-testid="user-search-input"
          className="q-input pl-9 pr-9"
          placeholder="Buscar @OndaTag o correo…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {q ? (
          <button
            type="button"
            onClick={() => setQ("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
            aria-label="Limpiar"
            data-testid="user-search-clear"
          >
            <X size={16} />
          </button>
        ) : null}
      </div>

      {state !== "idle" && (
        <div
          className="mt-2 rounded-2xl q-glass overflow-hidden"
          data-testid="user-search-results"
        >
          {state === "searching" && (
            <div className="p-4 flex items-center gap-2 text-sm text-slate-300">
              <Loader2 size={16} className="animate-spin" /> Buscando…
            </div>
          )}
          {state === "empty" && (
            <div className="p-4 text-sm text-slate-200" data-testid="user-search-empty">
              <div className="flex items-center gap-2 mb-1">
                <AtSign size={16} className="q-accent-text" />
                {isEmailQuery
                  ? "No se encontró ningún usuario con ese correo"
                  : "No se encontró ningún usuario con ese @OndaTag"}
              </div>
              <div className="text-xs text-slate-400">
                La búsqueda es exacta por <span className="font-mono">@OndaTag</span> o correo — no por nombre.
              </div>
            </div>
          )}
          {state === "ready" && (
            <ul className="divide-y divide-white/5">
              {results.map((u) => (
                  <li key={u.id}>
                    <button
                      type="button"
                      disabled={creating}
                      onClick={() => openChat(u.id)}
                      data-testid={`user-search-result-${u.ondatag}`}
                      className="w-full flex items-center gap-3 px-3 py-3 hover:bg-white/5 transition-colors text-left"
                    >
                      <OndaAvatar src={u.avatar} name={u.display_name} size={40} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-100 truncate flex items-center gap-1">
                          {u.display_name}
                          {u.is_verified_business && <VerifiedBadge size={12} />}
                        </div>
                        <div className="font-mono text-xs text-slate-400 truncate">
                          @{u.ondatag}
                        </div>
                      </div>
                      <span className="q-chip q-accent-text border-[color:var(--accent)]/40">
                        Chatear
                      </span>
                    </button>
                  </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
